import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { StatusBadge } from '../components/coreComponents/badge';
import { Button } from '../components/coreComponents/button';
import { SecretValue } from '../components/coreComponents/secretValue';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { NavTabs } from '../components/navTabs';
import { AccountBrandingTab } from './accountBrandingTab';
import { AccountConfigurationTab } from './accountConfigurationTab';
import { AccountPermissionsTab } from './accountPermissionsTab';
import { AccountStatisticsTab } from './accountStatisticsTab';

interface Account {
  readonly id: string;
  readonly alias?: string | null;
  readonly createdAt: string;
  readonly inviteLimit?: number | null;
  readonly inviteUseLimit?: number | null;
  readonly publishedAt?: string | null;
}

function OverviewTab({ account }: { account: Account }) {
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [publishOpen, setPublishOpen] = useState<boolean>(false);

  const isPublished = account.publishedAt != null;
  const publishInFlight = usePublishInFlight(account.id);
  const hasPendingSoftDelete = useAccountSoftDeletePending(account.id);

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr',
          gap: 3,
          maxWidth: 500,
          mb: 5
        }}
      >
        <Typography variant="labelSmall" color="text.secondary">
          ID
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {account.id}
        </Typography>

        <Typography variant="labelSmall" color="text.secondary">
          Alias
        </Typography>
        <Typography variant="body2">{account.alias ?? '-'}</Typography>

        <Typography variant="labelSmall" color="text.secondary">
          Created
        </Typography>
        <Typography variant="body2">{new Date(account.createdAt).toLocaleString()}</Typography>

        <Typography variant="labelSmall" color="text.secondary">
          Invite Limit
        </Typography>
        <Typography variant="body2">{account.inviteLimit ?? '-'}</Typography>

        <Typography variant="labelSmall" color="text.secondary">
          Invite Use Limit
        </Typography>
        <Typography variant="body2">{account.inviteUseLimit ?? '-'}</Typography>

        <Typography variant="labelSmall" color="text.secondary">
          Published
        </Typography>
        <Typography variant="body2">
          {account.publishedAt != null ? new Date(account.publishedAt).toLocaleString() : '-'}
        </Typography>
      </Box>

      <Stack direction="row" spacing={3}>
        {!isPublished && (
          <Button variant="primary" disabled={publishInFlight} onClick={() => setPublishOpen(true)}>
            {publishInFlight ? 'Publishing…' : 'Publish'}
          </Button>
        )}
        {!hasPendingSoftDelete && (
          <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
            Delete account
          </Button>
        )}
      </Stack>

      <Stack spacing={2} sx={{ mt: 5 }}>
        <TemporaryPasswordBanner accountId={account.id} />
        <PublishInProgressBanner accountId={account.id} />
        <PendingDeletionBanner accountId={account.id} />
        <DeletionInProgressBanner accountId={account.id} />
      </Stack>

      <PublishAccountDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        accountId={account.id}
      />

      <DeleteAccountDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        accountId={account.id}
      />
    </Box>
  );
}

interface PublishAccountDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
}

// Confirms the publish action before firing the mutation. The seed handler
// rotates the owner's password and emails it, so a stray click would
// invalidate any working credential the admin has been using for setup.
// Skips the preview query until the dialog opens — it does a cross-table
// scan and shouldn't fire on every overview render.
function PublishAccountDialog(props: PublishAccountDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [publishAccount, { loading }] = useMutation(accountPublishMutation, {
    refetchQueries: ['AccountOne', 'AccountProvisionJobs', 'AccountPublishJobs']
  });
  const { data: previewData, loading: previewLoading } = useQuery(accountPublishPreviewQuery, {
    variables: { id: props.accountId },
    skip: !props.open,
    fetchPolicy: 'cache-and-network'
  });

  const affected = previewData?.accountPublishPreview.affectedAccounts ?? [];

  const handleClose = () => {
    if (loading) return;
    setErrorMessage(null);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'text.secondary', fontWeight: 600 }}>Publish account</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            Rotates the owner's password and emails them the new sign-in details. The temporary
            password you've been using to set up this tenant will stop working immediately. If the
            owner already had credentials in another tenant, no password is sent — they'll get an
            email saying the account is ready and sign in with their existing password.
          </DialogContentText>

          {previewLoading && affected.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Checking other accounts for this owner…
            </Typography>
          )}

          {affected.length > 0 && (
            <Alert severity="warning">
              <Stack spacing={1}>
                <Typography variant="body2">
                  Publishing will also invalidate your admin access to{' '}
                  <strong>
                    {affected.length} other unpublished{' '}
                    {affected.length === 1 ? 'account' : 'accounts'}
                  </strong>{' '}
                  set up for this owner — the citadel password rotation is shared across every
                  tenant they belong to.
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  {affected.map(a => (
                    <Typography key={a.id} component="li" variant="body2">
                      {a.alias ?? <span style={{ fontFamily: 'monospace' }}>{a.id}</span>}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            </Alert>
          )}

          {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={loading}
          onClick={async () => {
            setErrorMessage(null);
            try {
              await publishAccount({ variables: { id: props.accountId } });
              props.onClose();
            } catch (err: any) {
              setErrorMessage(err.message ?? 'Failed to publish account');
            }
          }}
        >
          {loading ? 'Publishing…' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Shared poller for the publish-in-progress state. Apollo normalises by
// query + variables, so the OverviewTab and the in-progress banner share
// a single network round-trip per 5s tick. Returns the live job (or
// null) so both consumers can decide what to render.
function usePublishJob(accountId: string) {
  const { data } = useQuery(publishJobsQuery, {
    variables: {
      input: {
        type: 'publishAccount',
        entityType: 'account',
        entityId: accountId,
        statuses: ['pending', 'processing'],
        page: { limit: 1, offset: 0 }
      }
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });
  return data?.sqsJobMany.items[0] ?? null;
}

function usePublishInFlight(accountId: string): boolean {
  return usePublishJob(accountId) != null;
}

function PublishInProgressBanner({ accountId }: { accountId: string }) {
  const apollo = useApolloClient();
  const job = usePublishJob(accountId);

  // Detect the transition from "job in flight" → "job gone" (completed or
  // failed; the query filters to pending/processing so completion drops
  // the row). The publish mutation can't refetch AccountOne directly
  // because the seed handler writes Account.publishedAt async — the
  // mutation returns before the handler runs. We refetch here on the
  // trailing edge so the Publish button on the overview disappears as
  // soon as publishedAt is written.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (job != null) {
      wasInFlight.current = true;
      return;
    }
    if (wasInFlight.current) {
      wasInFlight.current = false;
      apollo.refetchQueries({ include: ['AccountOne', 'AccountProvisionJobs'] });
    }
  }, [job, apollo]);

  if (job == null) return null;

  return (
    <Alert severity="info" sx={{ mb: 4, maxWidth: 720 }}>
      <Typography variant="body2">
        Publishing in progress ({job.status}, {job.progress ?? 0}%). Rotating the owner's password
        and sending the welcome email — the page will refresh once it's done.
      </Typography>
    </Alert>
  );
}

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
}

function DeleteAccountDialog(props: DeleteAccountDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [requestDeletion, { loading }] = useMutation(accountDeleteRequestMutation, {
    refetchQueries: ['AccountPendingDeletion', 'PendingAdminActionMany']
  });
  // Skip the preview fetch until the dialog opens — it does a per-tenant
  // MySQL round-trip and shouldn't fire on every render of the overview tab.
  const {
    data: previewData,
    loading: previewLoading,
    error: previewError
  } = useQuery(accountDeletePreviewQuery, {
    variables: { id: props.accountId },
    skip: !props.open,
    fetchPolicy: 'cache-and-network'
  });

  const preview = previewData?.accountDeletePreview ?? null;

  const handleClose = () => {
    if (loading) return;
    setErrorMessage(null);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
        Request account deletion
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {previewError != null && (
            <Alert severity="error">
              Failed to load tenant preview: {previewError.message}. Aborting is recommended —
              proceeding without a preview means you can't verify the blast radius.
            </Alert>
          )}

          {previewLoading && preview == null && previewError == null && (
            <Typography variant="body2" color="text.secondary">
              Loading tenant preview…
            </Typography>
          )}

          {preview != null && (
            <Box
              sx={{
                p: 3,
                borderRadius: 1,
                border: 1,
                borderColor: 'error.light',
                borderLeftWidth: 4,
                borderLeftColor: 'error.main'
              }}
            >
              <Typography
                variant="labelSmall"
                color="text.secondary"
                sx={{ mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                You are about to request deletion of
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1 }}>
                <Typography variant="labelSmall" color="text.secondary">
                  Alias
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {preview.alias ?? '—'}
                </Typography>

                <Typography variant="labelSmall" color="text.secondary">
                  Owner
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {preview.owner}
                </Typography>

                <Typography variant="labelSmall" color="text.secondary">
                  Created
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {new Date(preview.createdAt).toLocaleString()}
                </Typography>

                <Typography variant="labelSmall" color="text.secondary">
                  Users
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                  {preview.userCount.toLocaleString()}
                </Typography>

                <Typography variant="labelSmall" color="text.secondary">
                  Organisations
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                  {preview.organisationCount.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}

          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            A confirmation email will be sent to the admin recipient list. Nothing is destroyed
            until someone clicks the link in that email and confirms. After confirmation the tenant
            database is torn down and citadel users are cleaned up — the per-tenant S3 backup stays
            restorable from the Recovery page for 180 days. To purge the backup (permanent delete),
            use the Recovery page after the soft delete completes.
          </DialogContentText>

          {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={loading || preview == null}
          onClick={async () => {
            setErrorMessage(null);
            try {
              await requestDeletion({
                variables: { accountId: props.accountId, kind: 'accountSoftDelete' }
              });
              props.onClose();
            } catch (err: any) {
              setErrorMessage(err.message ?? 'Failed to send deletion request');
            }
          }}
        >
          {loading ? 'Sending…' : 'Send confirmation email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Used by OverviewTab to hide the Delete account button when a
// soft-delete request is already in flight (clicking it would just
// hit the "request already pending" error from the resolver). Shares
// the same query Apollo cache as PendingDeletionBanner, so this
// doesn't add a second network call.
function useAccountSoftDeletePending(accountId: string): boolean {
  const { data } = useQuery(accountPendingDeletionQuery, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });
  return (
    data?.pendingAdminActionMany.items.some(
      item => item.accountId === accountId && item.kind === 'accountSoftDelete'
    ) ?? false
  );
}

interface PendingDeletionBannerProps {
  readonly accountId: string;
}

// Shows the open accountSoftDelete request for this account, if any.
// Polls so the banner disappears within ~5s of someone clicking the
// confirmation link from the email. Offers Cancel + Resend so the
// initiator can recover from a lost/expired email.
function PendingDeletionBanner({ accountId }: PendingDeletionBannerProps) {
  const apollo = useApolloClient();
  const { data, refetch } = useQuery(accountPendingDeletionQuery, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });

  const [cancelRequest, { loading: cancelling }] = useMutation(accountDeleteRequestCancelMutation);
  const [resendRequest, { loading: resending }] = useMutation(accountDeleteRequestResendMutation);

  const pending = data?.pendingAdminActionMany.items.find(
    item => item.accountId === accountId && item.kind === 'accountSoftDelete'
  );

  // When the pending row disappears (confirmed elsewhere), refetch the
  // jobs query so the in-progress banner can take over immediately.
  const wasPending = useRef(false);
  useEffect(() => {
    if (pending != null) {
      wasPending.current = true;
      return;
    }
    if (wasPending.current) {
      wasPending.current = false;
      apollo.refetchQueries({ include: ['AccountDeletionJobs'] });
    }
  }, [pending, apollo]);

  if (pending == null) return null;

  return (
    <Alert severity="warning" sx={{ maxWidth: 720 }}>
      <Stack spacing={1.5}>
        <Typography variant="body2">
          Deletion confirmation email sent — awaiting click-through. Initiated by{' '}
          <strong>{pending.initiatedByEmail}</strong> on{' '}
          {new Date(pending.createdAt).toLocaleString()}. Link expires{' '}
          {new Date(pending.expiresAt).toLocaleString()}.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="sm"
            variant="secondary"
            disabled={cancelling || resending}
            onClick={async () => {
              await cancelRequest({ variables: { id: pending.id } });
              await refetch();
            }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel request'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={cancelling || resending}
            onClick={async () => {
              await resendRequest({ variables: { id: pending.id } });
              await refetch();
            }}
          >
            {resending ? 'Resending…' : 'Resend email'}
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

function DeletionInProgressBanner({ accountId }: { accountId: string }) {
  const navigate = useNavigate();
  const { data } = useQuery(deletionJobsQuery, {
    variables: {
      input: {
        type: 'deleteAccount',
        entityType: 'account',
        entityId: accountId,
        statuses: ['pending', 'processing'],
        page: { limit: 1, offset: 0 }
      }
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });

  const job = data?.sqsJobMany.items[0];
  if (job == null) return null;

  const progress = job.progress ?? 0;

  return (
    <Alert severity="warning" sx={{ mb: 4, maxWidth: 720 }}>
      <Stack spacing={2}>
        <Typography variant="body2">
          Account deletion is in progress ({job.status}, {progress}%). Citadel cleanup runs in the
          seed worker; the per-tenant database teardown runs on CodeBuild. The global account record
          is removed once the job completes.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="secondary" onClick={() => navigate('/jobs')}>
            Open jobs
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

function TemporaryPasswordBanner({ accountId }: { accountId: string }) {
  const { data, refetch } = useQuery(provisionJobsQuery, {
    variables: {
      input: {
        type: 'provisionAccount',
        entityType: 'account',
        entityId: accountId,
        page: { limit: 5, offset: 0 }
      }
    },
    fetchPolicy: 'cache-and-network'
  });
  const [dismiss, { loading: dismissing }] = useMutation(dismissPasswordMutation);

  const jobWithPassword = data?.sqsJobMany.items.find(
    item => item.accountProvision?.temporaryPassword != null
  );
  // Surface the linked-existing-user banner only when there's no
  // temp-password banner already taking up the slot (otherwise both could
  // race for the same provision result if an admin provisions twice).
  const linkedJob =
    jobWithPassword == null
      ? data?.sqsJobMany.items.find(item => item.accountProvision?.linkedExistingUser === true)
      : null;

  if (jobWithPassword != null) {
    const details = jobWithPassword.accountProvision;
    if (details == null || details.temporaryPassword == null) return null;
    const password = details.temporaryPassword;
    const ownerName = `${details.ownerFirstName} ${details.ownerLastName}`.trim();

    return (
      <Alert severity="warning" sx={{ mb: 4, maxWidth: 720 }}>
        <Stack spacing={2}>
          <Typography variant="body2">
            Temporary password for <strong>{details.ownerEmail}</strong>
            {ownerName !== '' ? ` (${ownerName})` : ''} at{' '}
            <strong>{details.organisationName}</strong>.
          </Typography>
          <SecretValue value={password} />
          <Stack direction="row" spacing={2}>
            <Button
              variant="primary"
              disabled={dismissing}
              onClick={async () => {
                await dismiss({ variables: { id: jobWithPassword.id } });
                await refetch();
              }}
            >
              {dismissing ? 'Dismissing…' : 'Dismiss'}
            </Button>
          </Stack>
        </Stack>
      </Alert>
    );
  }

  if (linkedJob != null && linkedJob.accountProvision != null) {
    const details = linkedJob.accountProvision;
    return (
      <Alert severity="info" sx={{ mb: 4, maxWidth: 720 }}>
        <Typography variant="body2">
          <strong>{details.ownerEmail}</strong> already had credentials in another tenant — they
          were linked into <strong>{details.organisationName}</strong> without a temporary password.
          They can sign in with their existing password.
        </Typography>
      </Alert>
    );
  }

  return null;
}

export function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(accountOneQuery, {
    skip: id == null,
    variables: { id: id ?? '' }
  });

  if (id == null) return <Typography>Account not found</Typography>;
  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error: {error.message}</Typography>;

  const account: Account | null = data?.accountOne ?? null;
  if (account == null) return <Typography>Account not found</Typography>;

  return (
    <MainWrapper>
      <MainWrapperHeader
        title={account.alias ?? `Account ${account.id}`}
        description={account.alias ?? 'Account detail'}
        onBack={() => navigate('/')}
        showIds
        id={account.id}
        badge={
          account.publishedAt != null ? (
            <Tooltip
              arrow
              title={`Published on ${new Date(account.publishedAt).toLocaleString()}. Treat this account as production — changes here are irreversible.`}
            >
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <StatusBadge tone="error" label="PUBLISHED" isUppercase />
              </Box>
            </Tooltip>
          ) : (
            <StatusBadge tone="default" label="DRAFT" isUppercase />
          )
        }
      />

      <NavTabs
        tabs={[
          {
            value: 'overview',
            label: 'Overview',
            node: <OverviewTab account={account} />
          },

          {
            value: 'permissions',
            label: 'Permissions',
            node: <AccountPermissionsTab accountId={account.id} />
          },

          {
            value: 'statistics',
            label: 'Statistics',
            node: <AccountStatisticsTab accountId={account.id} />
          },
          {
            value: 'branding',
            label: 'Branding',
            node: <AccountBrandingTab accountId={account.id} />
          },
          {
            value: 'configuration',
            label: 'Configuration',
            node: (
              <AccountConfigurationTab
                accountId={account.id}
                publishedAt={account.publishedAt ?? null}
              />
            )
          }
        ]}
      />
    </MainWrapper>
  );
}

const accountOneQuery = gql<AccountOneGQL>`
  query AccountOne($id: ID!) {
    accountOne(id: $id) {
      id
      alias
      createdAt
      inviteLimit
      inviteUseLimit
      publishedAt
    }
  }
`;

const accountPublishMutation = gql<AccountPublishGQL>`
  mutation AccountPublish($id: ID!) {
    accountPublish(id: $id) {
      id
      type
      status
    }
  }
`;

const accountPublishPreviewQuery = gql<AccountPublishPreviewGQL>`
  query AccountPublishPreview($id: ID!) {
    accountPublishPreview(id: $id) {
      affectedAccounts {
        id
        alias
      }
    }
  }
`;

const publishJobsQuery = gql<AccountPublishJobsGQL>`
  query AccountPublishJobs($input: SqsJobManyInput) {
    sqsJobMany(input: $input) {
      items {
        id
        status
        progress
        createdAt
      }
    }
  }
`;

const provisionJobsQuery = gql<AccountProvisionJobsGQL>`
  query AccountProvisionJobs($input: SqsJobManyInput) {
    sqsJobMany(input: $input) {
      items {
        id
        createdAt
        accountProvision {
          ownerEmail
          ownerFirstName
          ownerLastName
          organisationName
          temporaryPassword
          linkedExistingUser
        }
      }
    }
  }
`;

const dismissPasswordMutation = gql<SqsJobDismissPasswordGQL>`
  mutation SqsJobDismissPassword($id: ID!) {
    sqsJobDismissPassword(id: $id) {
      id
      accountProvision {
        temporaryPassword
      }
    }
  }
`;

const accountDeleteRequestMutation = gql<AccountDeleteRequestGQL>`
  mutation AccountDeleteRequest($accountId: ID!, $kind: PendingAdminActionKind!) {
    accountDeleteRequest(accountId: $accountId, kind: $kind) {
      id
      kind
      status
      createdAt
      expiresAt
      initiatedByEmail
    }
  }
`;

// Used by both the per-account Pending banner and the global
// /pending-actions list. Apollo dedupes by operation name, so they share
// a single network round-trip per poll tick. Account-level banners
// filter the returned list client-side.
const accountPendingDeletionQuery = gql<AccountPendingDeletionGQL>`
  query AccountPendingDeletion {
    pendingAdminActionMany {
      items {
        id
        kind
        accountId
        accountAlias
        accountOwner
        initiatedByEmail
        createdAt
        expiresAt
        status
      }
    }
  }
`;

const accountDeleteRequestCancelMutation = gql<AccountDeleteRequestCancelGQL>`
  mutation AccountDeleteRequestCancel($id: ID!) {
    accountDeleteRequestCancel(id: $id) {
      id
      status
    }
  }
`;

const accountDeleteRequestResendMutation = gql<AccountDeleteRequestResendGQL>`
  mutation AccountDeleteRequestResend($id: ID!) {
    accountDeleteRequestResend(id: $id) {
      id
      expiresAt
      status
    }
  }
`;

const accountDeletePreviewQuery = gql<AccountDeletePreviewGQL>`
  query AccountDeletePreview($id: ID!) {
    accountDeletePreview(id: $id) {
      id
      alias
      owner
      createdAt
      userCount
      organisationCount
    }
  }
`;

const deletionJobsQuery = gql<AccountDeletionJobsGQL>`
  query AccountDeletionJobs($input: SqsJobManyInput) {
    sqsJobMany(input: $input) {
      items {
        id
        status
        progress
        createdAt
      }
    }
  }
`;
